import React, { useEffect, useState } from "react";
import { brand } from "../../../../../config/brands";

const HealthInsights = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const [content, setContent] = useState({
    title: "",
    overview: [],
    sections: [],
  });

  const handleViewMore = () => {
    setIsExpanded(true);
  };

  // Helper function to parse HTML content and preserve formatting
  const parseHTMLContent = (element) => {
    if (!element) return "";

    // Clone the element to avoid modifying the original
    const clonedElement = element.cloneNode(true);

    // Add special class to sub <ul> for circle bullet
    const nestedULs = clonedElement.querySelectorAll("ul ul");
    nestedULs.forEach((ul) => {
      ul.classList.add("sub-bullet");
    });

    const strongTags = clonedElement.querySelectorAll("strong");
    strongTags.forEach((strong) => {
      strong.style.fontWeight = "bold";
      strong.style.color = "#3F435E";
    });

    return clonedElement.innerHTML;
  };

  // Component to render HTML content safely
  const HTMLContent = ({ content, className = "", style = {} }) => {
    return (
      <div
        className={className}
        style={style}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_DYNAMIC_CONTENT_URL}/${brand.healthInsightsPath}/`
        );
        const htmlText = await res.text();

        // Parse the HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, "text/html");

        // Extract title (preserve HTML formatting)
        const titleElement = doc.querySelector("h2");
        const title = titleElement ? parseHTMLContent(titleElement) : "";

        // Extract overview paragraphs (preserve HTML formatting)
        const overviewDiv = doc.querySelector(".mb-6");
        const overviewParagraphs = Array.from(
          overviewDiv.querySelectorAll("p")
        ).map((p) => parseHTMLContent(p));

        // Extract all sections
        const sections = [];
        const allHeaders = doc.querySelectorAll("h3, h4");

        allHeaders.forEach((header) => {
          const section = {
            title: parseHTMLContent(header),
            level: header.tagName.toLowerCase(),
            content: [],
          };

          // Get next sibling elements until next header
          let nextElement = header.nextElementSibling;
          while (nextElement && !nextElement.matches("h3, h4")) {
            if (nextElement.matches("p")) {
              section.content.push({
                type: "paragraph",
                text: parseHTMLContent(nextElement),
              });
            } else if (nextElement.matches("ul")) {
              section.content.push({
                type: "html",
                html: parseHTMLContent(nextElement),
              });
            } else if (nextElement.matches("table")) {
              const rows = Array.from(nextElement.querySelectorAll("tr")).map(
                (row) =>
                  Array.from(row.querySelectorAll("td, th")).map((cell) =>
                    parseHTMLContent(cell)
                  )
              );
              section.content.push({
                type: "table",
                rows: rows,
              });
            }
            nextElement = nextElement.nextElementSibling;
          }

          sections.push(section);
        });

        setContent({
          title,
          overview: overviewParagraphs,
          sections,
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="container mt-4" style={{ backgroundColor: "#f8f9fb" }}>
      <div className="py-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12 d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <h4 className="mb-1" style={{ color: "#333", fontWeight: "600" }}>
                Health Insights
              </h4>
              <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                Stay Informed With The Latest Updates And Health Tips From Our
                Care Experts
              </p>
            </div>
            <a
              href={`${brand.webUrl}${brand.educationPath}`}
              className="text-primary text-decoration-none fw-bold hover-pointer"
              style={{ fontSize: "16px" }}
            >
              Explore Conditions & Treatments
            </a>
          </div>
        </div>

        {/* Main Card */}
        <div className="row">
          <div className="col-12">
            <div
              className="car shadow-sm border-0"
              style={{ borderRadius: "12px" }}
            >
              <div
                className="card-body p-4"
                style={{ backgroundColor: "#f7f7f7" }}
              >
                <HTMLContent
                  content={content.title}
                  className="card-title mb-4"
                  style={{ color: "#233876", fontWeight: "600" }}
                />

                {/* Overview Section */}
                <div className="mb-4">
                  {content.overview.map((paragraph, index) => (
                    <HTMLContent
                      key={index}
                      content={paragraph}
                      className="text-muted"
                      style={{ lineHeight: "1.6", marginBottom: "1rem" }}
                    />
                  ))}
                </div>

                {/* Initial visible sections */}
                {content.sections.slice(0, 2).map((section, index) => (
                  <div key={index} className="mb-4">
                    <HTMLContent
                      content={section.title}
                      className="mb-3"
                      style={{
                        color: "#233876",
                        fontWeight: "600",
                        fontSize: section.level === "h3" ? "1rem" : "0.9rem",
                      }}
                    />
                    {section.content.map((item, itemIndex) => {
                      if (item.type === "paragraph") {
                        return (
                          <HTMLContent
                            key={itemIndex}
                            content={item.text}
                            className="text-muted"
                            style={{ lineHeight: "1.6" }}
                          />
                        );
                      } else if (item.type === "html") {
                        return (
                          <HTMLContent
                            key={itemIndex}
                            content={item.html}
                            className="text-muted"
                            style={{ lineHeight: "1.6", marginBottom: "1rem" }}
                          />
                        );
                      } else if (item.type === "table") {
                        return (
                          <table
                            key={itemIndex}
                            className="table table-bordered"
                          >
                            <tbody>
                              {item.rows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                  {row.map((cell, cellIndex) => (
                                    <td
                                      key={cellIndex}
                                      dangerouslySetInnerHTML={{ __html: cell }}
                                    />
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        );
                      }
                      return null;
                    })}
                  </div>
                ))}

                {/* Expanded Content - Only show when isExpanded is true */}
                {isExpanded && (
                  <>
                    {content.sections.slice(2).map((section, index) => (
                      <div key={index} className="mb-4">
                        <HTMLContent
                          content={section.title}
                          className="mb-3"
                          style={{
                            color: "#233876",
                            fontWeight: "600",
                            fontSize:
                              section.level === "h3" ? "1rem" : "0.9rem",
                          }}
                        />
                        {section.content.map((item, itemIndex) => {
                          if (item.type === "paragraph") {
                            return (
                              <HTMLContent
                                key={itemIndex}
                                content={item.text}
                                className="text-muted"
                                style={{ lineHeight: "1.6" }}
                              />
                            );
                          } else if (item.type === "html") {
                            return (
                              <HTMLContent
                                key={itemIndex}
                                content={item.html}
                                className="text-muted"
                                style={{
                                  lineHeight: "1.6",
                                  marginBottom: "1rem",
                                }}
                              />
                            );
                          } else if (item.type === "table") {
                            return (
                              <table
                                key={itemIndex}
                                className="table table-bordered"
                              >
                                <tbody>
                                  {item.rows.map((row, rowIndex) => (
                                    <tr key={rowIndex}>
                                      {row.map((cell, cellIndex) => (
                                        <td
                                          key={cellIndex}
                                          dangerouslySetInnerHTML={{
                                            __html: cell,
                                          }}
                                        />
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            );
                          }
                          return null;
                        })}
                      </div>
                    ))}

                    {/* Close Button when expanded */}
                    <div className="text-end">
                      <button
                        className="btn btn-sm d-flex align-items-center ms-auto"
                        style={{
                          borderRadius: "5px",
                          fontSize: "14px",
                          backgroundColor: "#FDF2F2",
                          color: "#9E5B5B",
                          padding: "10px",
                          border: "none",
                        }}
                        onClick={() => setIsExpanded(false)}
                      >
                        <i className="fas fa-times me-2"></i>
                        Close
                      </button>
                    </div>
                  </>
                )}

                {/* View More Button - Only show when not expanded */}
                {!isExpanded && content.sections.length > 2 && (
                  <div className="text-end">
                    <button
                      className="btn btn-sm d-flex align-items-center ms-auto"
                      onClick={handleViewMore}
                      style={{
                        borderRadius: "5px",
                        fontSize: "14px",
                        backgroundColor: "#e1effe",
                        color: "#596B9C",
                        padding: "10px",
                        border: "none",
                      }}
                    >
                      <i className="fas fa-eye me-2"></i>
                      View More
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Font Awesome CDN */}
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css"
      />

      {/* Custom Styles */}
      <style>
        {`
          ul {
            list-style-type: disc;
          }
          ul.sub-bullet {
            list-style-type: circle;
            padding-left: 3rem;
          }
        `}
      </style>
    </div>
  );
};

export default HealthInsights;
